import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { EntityRatingCommentCount, Partner, Rating } from '@medprev-web-app/api-graphql-client';
import { ReactNode } from 'react';

import { EntitySection } from '@/app/(main)/(pages)/_components/entities/entity-section.component';
import { RatingCard } from '@/app/(main)/(pages)/_components/entities/entity-rating-card.component';
import { CommentLoading } from '@/app/(main)/(pages)/_components/entities/entity-comment-loading.component';
import {
  EntityLoadMoreComments,
  LoadCommentsPage,
} from '@/app/(main)/(pages)/_components/entities/entity-load-more-comments.component';
import { PARTNER_PROFILE_COMMENTS_PAGE_SIZE } from '@/app/(main)/(pages)/(institucional)/(index)/parceiros/_services/get-partner-profile-from-search-api.service';
import { RatingComment } from '@/types';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { cn } from '@/utils/cn-tw-merge';

interface PartnerRatingSectionProps {
  partner?: Partner | null;
  countComments?: EntityRatingCommentCount | null;
  initialComments?: RatingComment[];
  loadCommentsPage?: LoadCommentsPage;
  hasMoreComments?: boolean;
  commentsSort?: ReactNode;
  commentsKey?: string;
  isSortingComments?: boolean;
}

function getRating(partner: Partner): Rating | null {
  const rating = partner.facilities[0]?.rating;

  if (!rating) {
    return null;
  }

  return {
    ...rating,
    fiveStars: {
      tiers: {
        one: rating.fiveStars?.tiers.one || 0,
        two: rating.fiveStars?.tiers.two || 0,
        three: rating.fiveStars?.tiers.three || 0,
        four: rating.fiveStars?.tiers.four || 0,
        five: rating.fiveStars?.tiers.five || 0,
      },
      average: rating.fiveStars?.average || 0,
      count: rating.fiveStars?.count || 0,
    },
  };
}

export function PartnerRatingSection({
  partner,
  countComments,
  initialComments,
  loadCommentsPage,
  hasMoreComments,
  commentsSort,
  commentsKey,
  isSortingComments,
}: PartnerRatingSectionProps) {
  const rating = getRating(partner as Partner);
  const hasComments = Boolean(countComments) && Number(countComments?.total) > 0;

  return (
    <>
      <EntitySection
        data-testid={DataTestIdEnum.PARTNER_PROFILE_SECTION_RATING}
        className={cn(commentsSort && 'my-0')}
        removeHr={countComments?.total === 0 || Boolean(commentsSort)}
      >
        <RatingCard
          rating={rating as Rating}
          countComments={countComments}
          commentsSort={commentsSort}
        />
      </EntitySection>

      {hasComments && isSortingComments && (
        <div aria-busy data-testid={DataTestIdEnum.ENTITY_COMMENTS_LOADING}>
          {Array.from({ length: PARTNER_PROFILE_COMMENTS_PAGE_SIZE }).map((_, index) => (
            <CommentLoading key={index} />
          ))}
        </div>
      )}

      {hasComments && !isSortingComments && (
        <EntityLoadMoreComments
          key={commentsKey}
          countComments={countComments}
          partnerFacilityId={partner?.facilities[0]?.id as string}
          initialComments={initialComments}
          loadPage={loadCommentsPage}
          hasMore={hasMoreComments}
          className={cn(commentsSort && 'mb-0 md:mb-0')}
          commentClassName={cn(commentsSort && 'first:mt-3')}
          analyticsEventShowMore={AnalyticsEnum.CLICK_PARTNER_EXPAND_COMMENTARY}
          analyticsEventShowMoreComments={AnalyticsEnum.CLICK_PARTNER_SHOW_MORE_COMMENTARIES}
        />
      )}
    </>
  );
}
